import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import InvoiceService from '../../services/invoice.service';
import { countryCodes, getDefaultCountryCode, formatPhoneNumber } from '../../utils/countryCodes';
import ClientService from '../../services/client.service';
import VisitService from '../../services/visit.service';
import { useTranslation } from '../../hooks/useTranslation';

const InvoiceList = ({ onEdit, onView, onCreate }) => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [clients, setClients] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, invoice: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, invoice: null });
  const [cancelReason, setCancelReason] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [certifying, setCertifying] = useState({});
  const [emailDialog, setEmailDialog] = useState({ open: false, invoice: null });
  const [whatsappDialog, setWhatsappDialog] = useState({ open: false, invoice: null });
  const [emailData, setEmailData] = useState({ emails: [], message: '' });
  const [whatsappData, setWhatsappData] = useState({ countryCode: '+502', phone: '', message: '' });
  const [sending, setSending] = useState({ email: false, whatsapp: false });
  const [sendError, setSendError] = useState('');
  const { t } = useTranslation();


  useEffect(() => {
    loadInvoices();
  }, []);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await ClientService.getClients();
        const data = response.data?.data ?? response.data ?? [];
        setClients(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error loading clients:', e);
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [invoices, searchTerm, statusFilter, dateFilter, clientFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await InvoiceService.getInvoices();
      if (response.data.success) {
        setInvoices(response.data.data);
      }
    } catch (error) {
      setError('Failed to load invoices');
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = invoices;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(invoice =>
        invoice.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.id.toString().includes(searchTerm)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => {
        if (statusFilter === 'active') return !invoice.anulada;
        if (statusFilter === 'cancelled') return invoice.anulada;
        return true;
      });
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(invoice => {
        const invoiceDate = new Date(invoice.date);
        
        switch (dateFilter) {
          case 'today':
            return invoiceDate >= today;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return invoiceDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            return invoiceDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Client filter
    if (clientFilter !== 'all') {
      const clientId = Number(clientFilter);
      filtered = filtered.filter(invoice =>
        invoice.client_id === clientId || invoice.client?.id === clientId
      );
    }

    setFilteredInvoices(filtered);
  };

  const handleDelete = async () => {
    try {
      await InvoiceService.deleteInvoice(deleteDialog.invoice.id);
      await loadInvoices();
      setDeleteDialog({ open: false, invoice: null });
    } catch (error) {
      setError('Failed to delete invoice');
      console.error('Error deleting invoice:', error);
    }
  };

  const formatCurrency = (amount) => {
    const { formatCurrencySync } = require('../../utils/currencyUtils');
    return formatCurrencySync(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusChip = (invoice) => {
    if (invoice.anulada) {
      return <Chip label="Cancelled" color="error" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  const handleViewPDF = (invoice) => {
    if (!invoice.fel_pdf) {
      alert('FEL PDF not available. Please certify this invoice first.');
      return;
    }

    try {
      // Decode base64 PDF
      const byteCharacters = atob(invoice.fel_pdf);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create URL for the blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(blobUrl, '_blank');
      
      // Clean up the URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error opening PDF:', error);
      alert('Error opening PDF. Please try again.');
    }
  };

  const handleCancelInvoice = async () => {
    if (!cancelReason.trim()) {
      setCancelError('Please provide a reason for cancellation');
      return;
    }

    try {
      setCanceling(true);
      setCancelError('');
      
      const response = await InvoiceService.cancelInvoice(cancelDialog.invoice.id, cancelReason);
      
      if (response.data.success) {
        alert('Invoice cancelled successfully');
        setCancelDialog({ open: false, invoice: null });
        setCancelReason('');
        await loadInvoices(); // Refresh the list
      } else {
        setCancelError(response.data.message || 'Failed to cancel invoice');
      }
    } catch (error) {
      console.error('Error canceling invoice:', error);
      setCancelError(error.response?.data?.message || 'Failed to cancel invoice. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const canCancelInvoice = (invoice) => {
    return (
      invoice.fel_serie &&
      invoice.fel_numero &&
      invoice.fel_status === 'certificado' &&
      !invoice.anulada
    );
  };

  const isCertified = (invoice) => {
    return invoice.fel_status === 'certificado' && invoice.fel_uuid;
  };

  const canRetryCertification = (invoice) => {
    return !isCertified(invoice) && !invoice.anulada;
  };

  const handleRetryCertification = async (invoice) => {
    try {
      setCertifying(prev => ({ ...prev, [invoice.id]: true }));
      setError('');
      
      const response = await InvoiceService.certifyInvoice(invoice.id);
      
      if (response.data.success) {
        alert('Invoice certified successfully');
        await loadInvoices(); // Refresh the list
      } else {
        setError(response.data.message || 'Failed to certify invoice');
      }
    } catch (error) {
      console.error('Error certifying invoice:', error);
      setError(error.response?.data?.message || 'Failed to certify invoice. Please try again.');
    } finally {
      setCertifying(prev => ({ ...prev, [invoice.id]: false }));
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.emails.length || emailData.emails.some(email => !email.trim())) {
      setSendError('Please provide at least one valid email address');
      return;
    }

    try {
      setSending(prev => ({ ...prev, email: true }));
      setSendError('');
      
      const response = await InvoiceService.sendInvoiceEmail(emailDialog.invoice.id, {
        emails: emailData.emails.filter(email => email.trim()),
        message: emailData.message
      });
      
      if (response.data.success) {
        alert('Invoice sent via email successfully');
        setEmailDialog({ open: false, invoice: null });
        setEmailData({ emails: [], message: '' });
      } else {
        setSendError(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSendError(error.response?.data?.message || 'Failed to send email. Please try again.');
    } finally {
      setSending(prev => ({ ...prev, email: false }));
    }
  };

  const handleSendWhatsApp = async () => {
    if (!whatsappData.phone.trim()) {
      setSendError('Please provide a phone number');
      return;
    }

    try {
      setSending(prev => ({ ...prev, whatsapp: true }));
      setSendError('');
      
      // Format phone number with country code
      const fullPhoneNumber = formatPhoneNumber(whatsappData.countryCode, whatsappData.phone);
      
      const response = await InvoiceService.sendInvoiceWhatsApp(whatsappDialog.invoice.id, {
        phone: fullPhoneNumber,
        message: whatsappData.message
      });
      
      if (response.data && response.data.success !== false) {
        alert('Invoice sent via WhatsApp successfully');
        setWhatsappDialog({ open: false, invoice: null });
        setWhatsappData({ countryCode: getDefaultCountryCode(), phone: '', message: '' });
        setSendError('');
      } else {
        setSendError(response.data?.message || 'Failed to send WhatsApp message');
      }
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to send WhatsApp message. Please try again.';
      setSendError(errorMessage);
    } finally {
      setSending(prev => ({ ...prev, whatsapp: false }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
          {t ('invoices.tinvoice')}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
         {t ('invoices.msginvoice')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t ('invoices.sinvoice')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t ('common.status')}</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">{t ('services.allstatus')}</MenuItem>
                  <MenuItem value="active">{t ('common.active')}</MenuItem>
                  <MenuItem value="cancelled">{t ('invoices.Cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t ('common.date')}</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  label="Date"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">{t ('common.date')}</MenuItem>
                  <MenuItem value="today">{t ('serviceHistory.today')}</MenuItem>
                  <MenuItem value="week">{t ('serviceHistory.tweek')}</MenuItem>
                  <MenuItem value="month">{t ('serviceHistory.tmonth')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t ('clients.client')}</InputLabel>
                <Select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  label={t ('clients.client')}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">{t ('clients.allclients')}</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter('all');
                    setClientFilter('all');
                  }}
                  sx={{ borderRadius: 2 }}
                >
                 {t ('serviceHistory.clearfilters')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={onCreate}
                  sx={{
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    }
                  }}
                >
                  {t ('invoices.addInvoice')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell><strong>{t ('invoices.invoiceNumber')}</strong></TableCell>
                <TableCell><strong>{t ('clients.client')}</strong></TableCell>
                <TableCell><strong>{t ('common.date')}</strong></TableCell>
                <TableCell><strong>{t ('invoices.amount')}</strong></TableCell>
                <TableCell><strong>{t ('invoices.status')}</strong></TableCell>
                <TableCell><strong>{t ('common.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <ReceiptIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                      {t ('invoices.noInvoicesFound')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchTerm || statusFilter !== 'all' || dateFilter !== 'all' || clientFilter !== 'all'
                          ? 'Try adjusting your filters'
                          :  t ('invoices.createFirstInvoice')}
                      </Typography>
                      {!searchTerm && statusFilter === 'all' && dateFilter === 'all' && clientFilter === 'all' && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={onCreate}
                          sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            }
                          }}
                        >
                          {t ('invoices.addInvoice')}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                          {invoice.id}
                        </Avatar>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            INV-{invoice.id.toString().padStart(4, '0')}
                          </Typography>
                          {isCertified(invoice) && (
                            <Tooltip title="Certified">
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                            </Tooltip>
                          )}
                          {invoice.fel_error_message && !isCertified(invoice) && (
                            <Tooltip title={`Certification Error: ${invoice.fel_error_message}`}>
                              <ErrorIcon sx={{ color: 'error.main', fontSize: 20, cursor: 'help' }} />
                            </Tooltip>
                          )}
                          {canRetryCertification(invoice) && (
                            <Tooltip title={invoice.fel_error_message ? `Retry Certification - Error: ${invoice.fel_error_message}` : 'Retry Certification'}>
                              <IconButton
                                size="small"
                                onClick={() => handleRetryCertification(invoice)}
                                disabled={certifying[invoice.id]}
                                sx={{ 
                                  color: 'warning.main',
                                  padding: '4px',
                                  '&:hover': {
                                    backgroundColor: 'warning.lighter'
                                  }
                                }}
                              >
                                {certifying[invoice.id] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {invoice.client?.name || 'Unknown Client'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {invoice.client?.email || ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="body2">
                          {formatDate(invoice.date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {formatCurrency(invoice.total || 0)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(invoice)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Invoice">
                          <IconButton
                            size="small"
                            onClick={() => onView(invoice)}
                            sx={{ color: 'primary.main' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {invoice.fel_pdf && (
                          <Tooltip title="View FEL PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleViewPDF(invoice)}
                              sx={{ 
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.lighter'
                                }
                              }}
                            >
                              <PdfIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canRetryCertification(invoice) && (
                          <Tooltip title={invoice.fel_error_message ? `Retry Certification - Error: ${invoice.fel_error_message}` : 'Retry Certification'}>
                            <IconButton
                              size="small"
                              onClick={() => handleRetryCertification(invoice)}
                              disabled={certifying[invoice.id]}
                              sx={{ 
                                color: 'warning.main',
                                '&:hover': {
                                  backgroundColor: 'warning.lighter'
                                },
                                '&:disabled': {
                                  opacity: 0.5
                                }
                              }}
                            >
                              {certifying[invoice.id] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RefreshIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {isCertified(invoice) && (
                          <>
                            <Tooltip title="Send via Email">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const clientEmail = invoice.client?.email || '';
                                  setEmailData({ 
                                    emails: clientEmail ? [clientEmail] : [], 
                                    message: '' 
                                  });
                                  setEmailDialog({ open: true, invoice });
                                  setSendError('');
                                }}
                                sx={{ 
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'primary.lighter'
                                  }
                                }}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Send via WhatsApp">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const clientPhone = invoice.client?.phone || '';
                                  setWhatsappData({ 
                                    countryCode: getDefaultCountryCode(),
                                    phone: clientPhone, 
                                    message: '' 
                                  });
                                  setWhatsappDialog({ open: true, invoice });
                                  setSendError('');
                                }}
                                sx={{ 
                                  color: 'success.main',
                                  '&:hover': {
                                    backgroundColor: 'success.lighter'
                                  }
                                }}
                              >
                                <WhatsAppIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {canCancelInvoice(invoice) && (
                          <Tooltip title="Cancel FEL Invoice">
                            <IconButton
                              size="small"
                              onClick={() => setCancelDialog({ open: true, invoice })}
                              sx={{ 
                                color: 'error.dark',
                                '&:hover': {
                                  backgroundColor: 'error.lighter'
                                }
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isCertified(invoice) && (
                          <Tooltip title="Edit Invoice">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(invoice)}
                              sx={{ color: 'warning.main' }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Invoice">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteDialog({ open: true, invoice })}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, invoice: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete invoice INV-{deleteDialog.invoice?.id?.toString().padStart(4, '0')}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, invoice: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Invoice Dialog */}
      <Dialog 
        open={cancelDialog.open} 
        onClose={() => !canceling && setCancelDialog({ open: false, invoice: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CancelIcon sx={{ mr: 1 }} />
            Cancel FEL Invoice
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {cancelError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cancelError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            You are about to cancel a certified FEL invoice. This action will send a cancellation request to the SAT.
          </Typography>
          {cancelDialog.invoice && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              <strong>Invoice:</strong> {cancelDialog.invoice.fel_serie}-{cancelDialog.invoice.fel_numero}
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Cancellation *"
            placeholder="Please provide a detailed reason for cancelling this invoice..."
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              setCancelError('');
            }}
            disabled={canceling}
            required
            helperText="This reason will be sent to SAT and cannot be changed later"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setCancelDialog({ open: false, invoice: null });
              setCancelReason('');
              setCancelError('');
            }}
            disabled={canceling}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCancelInvoice}
            variant="contained"
            color="error"
            disabled={canceling || !cancelReason.trim()}
            startIcon={canceling ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {canceling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog 
        open={emailDialog.open} 
        onClose={() => !sending.email && setEmailDialog({ open: false, invoice: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1 }} />
            Send Invoice via Email
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            Send invoice {emailDialog.invoice && `INV-${emailDialog.invoice.id.toString().padStart(4, '0')}`} via email.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            label="Email Addresses *"
            placeholder="email1@example.com, email2@example.com"
            value={emailData.emails.join(', ')}
            onChange={(e) => {
              const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email);
              setEmailData({ ...emailData, emails });
              setSendError('');
            }}
            disabled={sending.email}
            required
            helperText="Enter email addresses separated by commas"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message (Optional)"
            placeholder="Add a custom message to include with the invoice..."
            value={emailData.message}
            onChange={(e) => {
              setEmailData({ ...emailData, message: e.target.value });
              setSendError('');
            }}
            disabled={sending.email}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setEmailDialog({ open: false, invoice: null });
              setEmailData({ emails: [], message: '' });
              setSendError('');
            }}
            disabled={sending.email}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={sending.email || !emailData.emails.length}
            startIcon={sending.email ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {sending.email ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send WhatsApp Dialog */}
      <Dialog 
        open={whatsappDialog.open} 
        onClose={() => !sending.whatsapp && setWhatsappDialog({ open: false, invoice: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WhatsAppIcon sx={{ mr: 1 }} />
            Send Invoice via WhatsApp
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            Send invoice {whatsappDialog.invoice && `INV-${whatsappDialog.invoice.id.toString().padStart(4, '0')}`} via WhatsApp.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Country Code *</InputLabel>
              <Select
                value={whatsappData.countryCode}
                onChange={(e) => {
                  setWhatsappData({ ...whatsappData, countryCode: e.target.value });
                  setSendError('');
                }}
                label="Country Code *"
                disabled={sending.whatsapp}
              >
                {countryCodes.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.flag} {country.code} - {country.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Phone Number *"
              placeholder="12345678"
              value={whatsappData.phone}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/\D/g, '');
                setWhatsappData({ ...whatsappData, phone: value });
                setSendError('');
              }}
              disabled={sending.whatsapp}
              required
              helperText="Enter phone number without country code"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message (Optional)"
            placeholder="Add a custom message to include with the invoice..."
            value={whatsappData.message}
            onChange={(e) => {
              setWhatsappData({ ...whatsappData, message: e.target.value });
              setSendError('');
            }}
            disabled={sending.whatsapp}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setWhatsappDialog({ open: false, invoice: null });
              setWhatsappData({ countryCode: getDefaultCountryCode(), phone: '', message: '' });
              setSendError('');
            }}
            disabled={sending.whatsapp}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendWhatsApp}
            variant="contained"
            disabled={sending.whatsapp || !whatsappData.phone.trim()}
            startIcon={sending.whatsapp ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
            sx={{
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #20ba5a 0%, #0f7a6d 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {sending.whatsapp ? 'Sending...' : 'Send WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceList;
